import { colors } from "@/shared/design-system";
import { AlomainLogo, AppText, Icon, ThemeToggle } from "@/shared/ui";

type Props = {
  /** Prénom du client. Absent tant que la session n'est pas hydratée. */
  firstName?: string;
  /** Toute 1ʳᵉ ouverture de la Home → message d'accueil au lieu de « revoir ». */
  isFirstVisit?: boolean;
  hasNotification?: boolean;
  /** Ouvre le chat support tawk.to. */
  onSupportPress: () => void;
  isSupportOpening?: boolean;
  onNotificationsPress: () => void;
  /**
   * `false` quand la marque est déjà affichée ailleurs à l'écran — c'est le cas
   * au-delà de md, où la sidebar porte le logo accolé au nom « Alomain ». Deux
   * logos visibles en même temps sur la même ligne se lisaient comme un doublon.
   */
  showLogo?: boolean;
};

/**
 * Barre supérieure de la Home — v4.
 *
 * Logo de marque + salutation à gauche, cloche de notifications à droite. La
 * salutation avait cédé la place au logo le 2026-08-04, elle est revenue à côté
 * de lui sur la même ligne. La recherche n'est pas ici : elle vit dans la
 * `CommandBar`, qui porte l'action n°1 de l'app.
 */
export function HomeTopBar({
  firstName,
  isFirstVisit = false,
  hasNotification = false,
  onSupportPress,
  isSupportOpening = false,
  onNotificationsPress,
  showLogo = true,
}: Props) {
  return (
    <div className="flex flex-row items-center justify-between gap-3 py-2">
      {/* Logo + salutation sur UNE ligne : l'identité de marque d'abord, le
          prénom ensuite. Séparés par un filet, pour que « Salut X » ne se lise
          pas comme la signature du logo. */}
      <div className="flex min-w-0 flex-row items-center gap-2.5">
        {showLogo && <AlomainLogo height={26} />}
        {/* Pas de nom inventé : sans prénom, on n'affiche RIEN plutôt qu'un
            « Salut Bienvenue » qui sonnerait faux. */}
        {firstName ? (
          <>
            {/* Le filet ne sépare que s'il y a deux choses à séparer. */}
            {showLogo && <span aria-hidden className="h-7 w-px shrink-0 bg-gray-200" />}
            <div className="min-w-0">
              <AppText as="p" variant="labelBold" color={colors.navy800} className="truncate">
                Salut {firstName}
              </AppText>
              {/* Le sous-titre change à la 1ʳᵉ visite : « Content de vous
                  revoir » à quelqu'un qui n'est jamais venu sonnerait faux. */}
              <AppText as="p" variant="tiny" color={colors.gray500} className="truncate">
                {isFirstVisit ? "Bienvenue sur Alomain !" : "Content de vous revoir !"}
              </AppText>
            </div>
          </>
        ) : null}
      </div>

      {/* Actions de la barre : thème puis notifications. L'interrupteur vit ICI
          et nulle part ailleurs — cette barre est rendue par le shell au-delà
          de md et par l'accueil en mobile, elle couvre donc les deux formats
          avec un seul point de montage. */}
      <div className="flex shrink-0 flex-row items-center gap-0.5">
        <ThemeToggle />

        <button
          type="button"
          onClick={onSupportPress}
          disabled={isSupportOpening}
          aria-label={isSupportOpening ? "Ouverture du support" : "Contacter le support"}
          className="flex h-11 w-11 shrink-0 items-center justify-center rounded-full transition-colors hover:bg-gray-50 disabled:cursor-wait disabled:opacity-50"
        >
          <Icon name="chatbubble-ellipses-outline" size={21} color={colors.navy800} />
        </button>

        <button
          type="button"
          onClick={onNotificationsPress}
          aria-label={hasNotification ? "Notifications, non lues" : "Notifications"}
          className="relative flex h-11 w-11 shrink-0 items-center justify-center rounded-full transition-colors hover:bg-gray-50"
        >
          <Icon name="notifications-outline" size={21} color={colors.navy800} />
          {hasNotification && (
            <span
              aria-hidden
              className="absolute right-2.5 top-2.5 h-2 w-2 rounded-full border-2 border-white bg-sos-600"
            />
          )}
        </button>
      </div>
    </div>
  );
}
